import { useClearReportViewOnLeave } from "../useClearReportViewOnLeave";
import { Fragment, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DynamicGrid from "../../../../components/DynamicGrid/DynamicGrid";
import { analyticsGridKey } from "../../../../utils/helpers/analyticsGridKey";
import { BigBoldText } from "../../dashboard/home/home.style";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { BackBtn } from "../../Users/components/logIncident/logIncident.style";
import { PDFExport } from "@progress/kendo-react-pdf";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
  ExcelExportColumnGroup,
} from "@progress/kendo-react-excel-export";
import {
  useAnalyticsWrapper,
  useReportWrapper,
  useStoreWrapper,
} from "../../../../store/store";
import {
  ALL_INCIDENTS_OPTION,
  addTotal,
  convertDataForLineChart,
  getFilteredIncident,
  getTableCols,
  getTableData,
  getUniqueIncidentTypes,
  modifyTitle,
  resolveIncidentTypeSelection,
  sortIncidentType,
  transformRepeatIncidentsForGrid,
  transformInjuryReportData,
} from "../../../../utils/helpers/analyticsUtils";
import type { IncidentTypeOption } from "../../../../utils/helpers/analyticsUtils";
import { MapContainer } from "../style";
import LineChart from "../../../../components/LineChart";
import useIncident from "../../../../hooks/useIncident";

import {
  incidentListReport,
  repeatIncidentReport,
  statusReport,
  injuryReportCols,
} from "../dummyData/colums";
import useIncidentTypes from "../../../../hooks/useIncidentTypes";
import { viewClickedIncident } from "../../../../utils/helpers/incidentListUtils";
import moment from "moment";
import { TitleContainer } from "../fall-analysis/style";
// NOTE: FilterModal is shared across all analytics reports (incident-list, status, repeat, trend, wound-analysis)
// despite living in fall-analysis folder. Consider renaming path for clarity.
import FilterModal from "../fall-analysis/FilterModal";
import { TitleBar } from "../../../../components/DynamicGrid/DynamicGrid.styles";

import {
  DateDisplayModifyBtnContainer,
  DateModBtnViewBtnContainer,
  HeaderSection,
  ModifyBtn,
  ModifyComponent,
  ViewandExportReportBtnContainer,
  ViewBtnsContainer,
} from "../fall-analysis/FallAnalysisReport";
import { GoPencil } from "react-icons/go";
import { Button, MenuProps } from "antd";
import { FaBell } from "react-icons/fa6";
import styled from "styled-components";
import Modalant from "../../../../components/ModalA/Modalant";
import EmailSubscriptionForm from "../fall-analysis/form/EmailSubForm";
import useSubscriptions from "../../../../hooks/useSubscriptions";
import { toast } from "react-toastify";

const ActBtn = styled.button`
  color: #b580ff;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  cursor: pointer;
`;

const TableContainer = styled.div`
  margin: 16px 0;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  & .k-grid {
    min-width: 1200px;
  }
`;

// PDF export-related layout constants for top level exports containing multiple tables
const EXPORT_OFFSCREEN_LEFT = "-4000px";
const EXPORT_BANNER_GAP = "8px";
const EXPORT_SECTION_SPACING = "30px";

const ExportBtn = styled(Button)`
  background-color: var(--main-purple-light) !important;
  border-color: var(--main-purple-light) !important;
  color: #fff !important;
`;

const Report = () => {
  useClearReportViewOnLeave("report");
  const navigate = useNavigate();

  const [trendsCol, setTrendsCol] = useState<any[]>([]);
  const [trendsData, setTrendsData] = useState<any[]>([]);
  const [lineChartData, setLineChartData] = useState<any>();

  const [month, setMonth] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [incidentType, setIncidentType] = useState<string>("");

  const [repeatIncidentGroups, setRepeatIncidentGroups] = useState<any[]>([]);
  const repeatIncidentColumns = useMemo(() => repeatIncidentReport(), []);

  // Generic: Track visible optional columns per grid per report type for top-level exports
  // Structure: { reportType: { gridKey: [visibleFieldNames] } }
  // This allows each grid to update independently and we aggregate at export time
  const [visibleOptionalColumnsByGrid, setVisibleOptionalColumnsByGrid] = useState<
    Record<string, Record<string, string[]>>
  >({});

  // Generic: Callback factory to track optional column visibility for any report type
  // Returns a handler that updates this specific grid's visible fields
  const createOptionalColumnsChangeHandler = useCallback(
    (reportType: string, gridKey: string) => {
      return (visibleFields: string[]) => {
        setVisibleOptionalColumnsByGrid((prev) => {
          const reportGrids = prev[reportType] || {};
          return {
            ...prev,
            [reportType]: {
              ...reportGrids,
              [gridKey]: visibleFields,
            },
          };
        });
      };
    },
    []
  );

  // Derive column defs from repeatIncidentReport for top-level export
  // Only include optional columns if they're visible in at least one grid
  const repeatIncidentExcelColumnDefs = useMemo(() => {
    const reportCols = repeatIncidentReport();
    
    // Aggregate visible optional fields across all grids for this report
    const gridsData = visibleOptionalColumnsByGrid['repeat-incident'] || {};
    const allVisibleFields = new Set<string>();
    Object.values(gridsData).forEach((fields) => {
      fields.forEach((field) => allVisibleFields.add(field));
    });

    return [
      // Add Facility and Resident as first columns for clarity in flattened export
      { field: "FacilityName", title: "Facility" },
      { field: "ResidentName", title: "Resident" },
      // Map report columns, filtering optional ones based on visibility
      ...reportCols
        .filter((col: any) => {
          // Include non-optional columns always
          if (!col.optional) return true;
          // Include optional columns only if visible in at least one grid
          return allVisibleFields.has(col.accessor);
        })
        .map((col: any) => ({
          field: col.accessor,
          title: col.Header,
        })),
    ];
  }, [visibleOptionalColumnsByGrid]);

  /* 
   * PATTERN: To add top-level export for other reports (e.g., incident-list, status-report):
   * 
   * 1. Create column defs (similar to repeatIncidentExcelColumnDefs above):
   *    const incidentListExcelColumnDefs = useMemo(() => {
   *      const reportCols = incidentListReport();
   *      const gridsData = visibleOptionalColumnsByGrid['incident-list'] || {};
   *      const allVisibleFields = new Set<string>();
   *      Object.values(gridsData).forEach(fields => fields.forEach(f => allVisibleFields.add(f)));
   *      return reportCols.filter(col => !col.optional || allVisibleFields.has(col.accessor))...
   *    }, [visibleOptionalColumnsByGrid]);
   * 
   * 2. Pass callback to DynamicGrid in the render section with unique gridKey:
   *    <DynamicGrid
   *      ...props
   *      onOptionalColumnsChange={createOptionalColumnsChangeHandler('incident-list', gridUniqueKey)}
   *    />
   * 
   * 3. Create top-level export refs and buttons (similar to repeat-incident pattern below)
   */


  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);

  const [selectedIncident, setSelectedIncident] = useState<any>({});

  // Ref for combined PDF export of repeat incidents
  const repeatIncidentPDFExportRef = useRef<PDFExport>(null);
  // Ref for combined Excel export of repeat incidents
  const repeatIncidentExcelExportRef = useRef<ExcelExport>(null);

  const incidentTrendExcelExportRef = useRef<ExcelExport>(null);
  const incidentTrendPDFExportRef = useRef<PDFExport>(null);

  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  const { incidentTypes: incidentTypesObject } = useIncidentTypes();

  const {
    setAnalyticsIncidentList,
    setAnalyticsIncidentTrend,
    setAnalyticsRepeatIncident,
    analyticsIncidentList,
    analyticsIncidentTrend,
    analyticsRepeatIncident,
    analyticsStatusReport,
    analyticsIncidentRate,
    analyticsInjuryReport,
    setAnalyticsInjuryReport,
    setReportType,
    trendSelectedFacility,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setGlobalAnalyticsSelectedIncidentType,
    setGlobalAnaylyticsFacility,
    setGlobalAnalyticsSelectedResidentStatus,
    globalAnalyticsSelectedIncidentType,
    globalAnaylyticsFacility,
    globalAnalyticsSelectedResidentStatus,
    globalAnalyticsSelectedInjuryType,
    grouping,
  } = useAnalyticsWrapper((state) => ({
    setAnalyticsIncidentList: state.setAnalyticsIncidentList,
    setAnalyticsIncidentTrend: state.setAnalyticsIncidentTrend,
    setAnalyticsRepeatIncident: state.setAnalyticsRepeatIncident,
    analyticsIncidentList: state.analyticsIncidentList,
    analyticsIncidentTrend: state.analyticsIncidentTrend,
    analyticsRepeatIncident: state.analyticsRepeatIncident,
    analyticsIncidentRate: state.analyticsIncidentRate,
    analyticsStatusReport: state.analyticsStatusReport,
    analyticsInjuryReport: state.analyticsInjuryReport,
    setAnalyticsInjuryReport: state.setAnalyticsInjuryReport,
    setReportType: state.setReportType,
    trendSelectedFacility: state.trendSelectedFacility,
    startDate: state.startDate,
    endDate: state.endDate,
    setStartDate: state.setStartDate,
    setEndDate: state.setEndDate,
    setGlobalSelectedFacility: state.setGlobalSelectedFacility,
    setGlobalAnalyticsSelectedIncidentType:
      state.setGlobalAnalyticsSelectedIncidentType,
    setGlobalAnaylyticsFacility: state.setGlobalAnaylyticsFacility,
    setGlobalAnalyticsSelectedResidentStatus:
      state.setGlobalAnalyticsSelectedResidentStatus,
    globalAnalyticsSelectedIncidentType:
      state.globalAnalyticsSelectedIncidentType,
    globalAnaylyticsFacility: state.globalAnaylyticsFacility,
    globalAnalyticsSelectedResidentStatus:
      state.globalAnalyticsSelectedResidentStatus,
    grouping: state.grouping,
    globalAnalyticsSelectedInjuryType:
      state.globalAnalyticsSelectedInjuryType,
  }));

  const { setIncidentsFromTrends, createForm, updateForm, user } =
    useStoreWrapper((state) => ({
      setIncidentsFromTrends: state.setIncidentsFromTrends,
      user: state.user,
      createForm: state.createForm,
      updateForm: state.updateForm,
      setOpenIncidentClicked: state.setOpenIncidentClicked,
      userFacilities: state.userFacilities,
    }));

  const { setIncident } = useReportWrapper((state) => ({
    setIncident: state.setIncident,
  }));

  const { handlePostSubscription } = useSubscriptions({});

  const { isLoading: isLoadingSubscription, mutateAsync: doPostSubscription } =
    handlePostSubscription;

  const { incidentsByTrends, getIncidentDetailsWithId } = useIncident({
    facilityId: trendSelectedFacility?.value,
    incidentType: incidentType,
    month: month,
    year: year,
    selectedIncidentId: selectedIncident?.incidentId,
  });

  const {
    refetch: refetchSelectedIncident,
    isFetching: isFetchingSelectedIncident,
  } = getIncidentDetailsWithId;

  const { data: incidentsTrendsData } = incidentsByTrends;

  useEffect(() => {
    if (incidentsTrendsData) {
      setIncidentsFromTrends(incidentsTrendsData.data.data);
      navigate("/user/incidents-list-by-trends");
    }
  }, [incidentsTrendsData]);

  const [params] = useSearchParams();
  const reportType = params.get("report") ?? "";

  const incidentTypeOptions = useMemo<IncidentTypeOption[]>(() => {
    if (!Array.isArray(incidentTypesObject)) return [];

    return incidentTypesObject
      .map((incident: any) => ({
        label: incident?.label ?? "",
        value: String(
          incident?.flag ?? incident?.value ?? incident?.label ?? ""
        ),
      }))
      .filter((option) => option.label && option.value);
  }, [incidentTypesObject]);

  const incidentTypeSelectionSource = useMemo(
    () => globalAnalyticsSelectedIncidentType,
    [globalAnalyticsSelectedIncidentType]
  );

  const {
    labels: selectedIncidentTypes,
    isAllSelected: isAllIncidentTypesSelected,
  } = useMemo(
    () =>
      resolveIncidentTypeSelection(
        incidentTypeSelectionSource,
        incidentTypeOptions
      ),
    [incidentTypeSelectionSource, incidentTypeOptions]
  );

  // Sync + persisted, unlike the label (which waits on the incident-types query).
  const incidentTypeSelectionKey = JSON.stringify(
    incidentTypeSelectionSource ?? null
  );

  const selectedIncidentTypesLabel = isAllIncidentTypesSelected
    ? ALL_INCIDENTS_OPTION.label
    : selectedIncidentTypes.length
      ? selectedIncidentTypes.join(", ")
      : "";

  const handleBack = () => {
    setAnalyticsIncidentList(null);
    setAnalyticsIncidentTrend(null);
    setAnalyticsRepeatIncident(null);
    setReportType("");
    setStartDate("");
    setEndDate("");
    navigate("/user/analytics");
    setGlobalAnalyticsSelectedIncidentType(null);
    setGlobalAnaylyticsFacility(null);
    setGlobalAnalyticsSelectedResidentStatus([]);
  };

  useEffect(() => {
    if (selectedIncident?.incidentId?.length > 0) {
      const refetchedData = async () => {
        try {
          const response = await refetchSelectedIncident();
          const selectedIncident = response?.data?.data?.data;
          const incidentType = incidentTypesObject.find(
            (incident: any) => incident.flag === selectedIncident.RecordTypeId
          );
          setIncident(selectedIncident);
          viewClickedIncident(
            createForm,
            updateForm,
            selectedIncident,
            incidentType,
            navigate
          );
        } catch (err) {
          console.log({ err });
        }
      };
      refetchedData();
    }
  }, [selectedIncident]);

  useEffect(() => {
    if (analyticsIncidentTrend !== null) {
      const filteredData = getFilteredIncident(analyticsIncidentTrend);

      const uniqueIncidentTypes = getUniqueIncidentTypes(filteredData).sort(
        (a: any, b: any) => {
          if (a === null) {
            return -1;
          }
          if (b === null) {
            return 1;
          }
          return a?.localeCompare(b);
        }
      );
      const tableCol = getTableCols(filteredData, grouping);

      let tableData = getTableData(uniqueIncidentTypes, filteredData, grouping);

      const lineChartData = convertDataForLineChart(tableData);

      tableData = addTotal(tableData);

      // Replace null incidentType with "Total"
      const processedTableData = () => {
        return tableData.map((item: any) => ({
          ...item,
          incidentType:
            item.incidentType === null ? "Total" : item.incidentType,
        }));
      };

      const allData = processedTableData();
      const dataWithoutFooter = allData.filter((item: any) => item.incidentType !== "Total");

      // Calculate footer row dynamically
      const footerRow: any = { incidentType: "Total" };
      if (dataWithoutFooter.length > 0) {
        const keys = Object.keys(dataWithoutFooter[0]).filter(
          (k) => k !== "incidentType" && typeof dataWithoutFooter[0][k] === "number"
        );
        keys.forEach((key) => {
          footerRow[key] = dataWithoutFooter.reduce(
            (sum: number, row: any) => sum + (row[key] || 0),
            0
          );
        });
      }

      const allTrendData = dataWithoutFooter.sort(sortIncidentType).concat(footerRow);
      
      setTrendsCol(tableCol);
      setTrendsData(allTrendData);
      setLineChartData(lineChartData);
    }
  }, [analyticsIncidentTrend]);

  if (
    !analyticsIncidentList &&
    !analyticsIncidentTrend &&
    !analyticsRepeatIncident &&
    !analyticsIncidentRate &&
    !analyticsStatusReport &&
    !analyticsInjuryReport
  ) {
    navigate("/user/analytics");
  }
  
  // This is used for reports that have multiple tables and the export will include all tables
  const triggerExcelDownload = () => {
    if (reportType === "repeat-incident") {
      // Use Kendo ExcelExport for proper .xlsx format matching DynamicGrid
      if (repeatIncidentExcelExportRef.current) {
        repeatIncidentExcelExportRef.current.save();
      }
    } else if (reportType === "incident-trend") {
      if (incidentTrendExcelExportRef.current) {
        incidentTrendExcelExportRef.current.save();
      }
    }
  };
  // add the type of the report to the file name
  const triggerPDFDownload = () => {
    if (reportType === "repeat-incident") {
      // Use Kendo PDFExport for proper styling matching individual grids
      if (repeatIncidentPDFExportRef.current) {
        repeatIncidentPDFExportRef.current.save();
      }
    } else if (reportType === "incident-trend") {
      if (incidentTrendPDFExportRef.current) {
        incidentTrendPDFExportRef.current.save();
      }
    }
  };

  useEffect(() => {
    if (analyticsRepeatIncident !== null) {
      const groupedData = transformRepeatIncidentsForGrid(
        analyticsRepeatIncident
      );
      setRepeatIncidentGroups(groupedData);
    } else {
      setRepeatIncidentGroups([]);
    }
  }, [analyticsRepeatIncident]);

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (showFilterModal && !target.closest(".modify-btn-container")) {
      setShowFilterModal(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showFilterModal]);

  const items: MenuProps["items"] =
    reportType === "incident-rate"
      ? []
      : [
          {
            key: "1",
            label: (
              <Button
                onClick={(prev) => setShowSubscriptionModal((prev) => !prev)}
                className="flex w-full items-center border-0"
              >
                <FaBell /> Subscribe
              </Button>
            ),
          },
        ];

  const getReportType = () => {
    if (reportType === "incident-list") return "Incident List";
    if (reportType === "repeat-incident") return "Repeat Incident";
    if (reportType === "incident-trend") return "Trend Report";
    if (reportType === "status-report") return "Status Report";
    if (reportType === "injury-report") return "Injury Report";
    return "";
  };

 const getIncidentTypes = () => {
  if (
    reportType === "incident-list" ||
    reportType === "repeat-incident" ||
    reportType === "incident-trend" ||
    reportType === "injury-report"
  ) {
    if (selectedIncidentTypes.length) {
      return selectedIncidentTypes.join(",");
    }
  }
  // return undefined if user did not explicitly select incident types
  return undefined;
};

  const getResidentStatus = () => {
    if (reportType === "repeat-incident") {
      if (globalAnalyticsSelectedResidentStatus?.length) {
        return globalAnalyticsSelectedResidentStatus.join(";");
      }
    }
    // return undefined if user did not explicitly select resident status
    return undefined;
  };

const getInjuryTypes = () => {
  if (reportType === "injury-report") {
    if (globalAnalyticsSelectedInjuryType?.length) {
      return globalAnalyticsSelectedInjuryType.join(";");
    }
  }
  // return undefined if user did not explicitly select injury types
  return undefined;
};

  const repeatIncidentExportRows = useMemo(() => {
    if (reportType !== "repeat-incident" || !repeatIncidentGroups?.length) {
      return [];
    }

    return repeatIncidentGroups.flatMap((facility: any) =>
      facility.residents.flatMap((resident: any) =>
        resident.incidents.map((incident: any) => {
          return {
            FacilityName:
              facility?.facilityName ?? incident?.FacilityName ?? "-",
            ResidentName:
              resident?.residentName ?? incident?.ResidentName ?? "-",
            IncidentName: incident?.IncidentName ?? "-",
            IncidentType: incident?.IncidentType ?? "-",
            FallType: incident?.FallType ?? "-",
            DayOfTheWeek: incident?.DayOfTheWeek ?? "-",
            FacilityDate: incident?.FacilityDate ?? "-",
            FacilityTime: incident?.FacilityTime ?? "-",
            Location: incident?.Location ?? "-",
            Unit: incident?.Unit ?? "-",
            Injuries:
              incident?.Injuries === null || incident?.Injuries === undefined
                ? "-"
                : incident?.Injuries,
            SentToAcuteCareFacility: incident?.SentToAcuteCareFacility ?? "-",
          };
        })
      )
    );
  }, [repeatIncidentGroups, reportType]);

  // Transform injury report data to handle nested Injuries array
  const transformedInjuryReport = useMemo(() => {
    return transformInjuryReportData(analyticsInjuryReport);
  }, [analyticsInjuryReport]);

  const handleFormSubmit = async (data: any) => {
    // Validation: Facilities must be selected for all report types
    if (!globalAnaylyticsFacility || globalAnaylyticsFacility.length === 0) {
      toast.error(
        "Please select at least one facility before subscribing to a report."
      );
      return;
    }

    // Validation: Repeat incident reports require incident types and resident status
    if (reportType === "repeat-incident") {
      const incidentTypes = getIncidentTypes();
      const residentStatus = getResidentStatus();

      if (!incidentTypes || incidentTypes.trim() === "") {
        toast.error(
          "Please select at least one incident type before subscribing to a repeat incident report."
        );
        return;
      }

      if (!residentStatus || residentStatus.trim() === "") {
        toast.error(
          "Please select at least one resident status before subscribing to a repeat incident report."
        );
        return;
      }
    }

    // Validation: Incident list and trend reports require incident types
    if (reportType === "incident-list" || reportType === "incident-trend") {
      const incidentTypes = getIncidentTypes();

      if (!incidentTypes || incidentTypes.trim() === "") {
        toast.error(
          `Please select at least one incident type before subscribing to ${reportType === "incident-list" ? "an incident list" : "a trend"} report.`
        );
        return;
      }
    }

    // Validation: Injury reports require incident types and injury types
    if (reportType === "injury-report") {
      const incidentTypes = getIncidentTypes();
      const injuryTypes = getInjuryTypes();
      
      if (!incidentTypes || incidentTypes.trim() === "") {
        toast.error("Please select at least one incident type before subscribing to an injury report.");
        return;
      }
      
      if (!injuryTypes || injuryTypes.trim() === "") {
        toast.error("Please select at least one injury type before subscribing to an injury report.");
        return;
      }
    }

    const subscription = {
      Report_Type__c: getReportType(),
      Start_Date__c: data.startDate,
      End_Date__c: data.endDate,
      Interval_Type__c: data.recurrence,
      Description__c: data.description,
      User__c: user.userId,
      Facilities__c: globalAnaylyticsFacility.join(","),
      ...(data.recurrence === "weekly" && {
        Day_Of_The_Week__c: data.weeklyDays,
      }),
      ...(data.recurrence === "monthly" && {
        Day_Of_The_Month__c: data.monthlyDay,
      }),

      // Always include Incident_Types__c; getIncidentTypes() returns undefined for reports that don't use it
      Incident_Types__c: getIncidentTypes(),

      ...(reportType === "incident-trend" && {
        Facility__c: globalAnaylyticsFacility.join(","),
      }),
      // Always include Resident_Status__c for repeat incident; getResidentStatus() returns undefined for other reports
      Resident_Status__c: getResidentStatus(),
      // Always include Injury_Types__c for injury report; getInjuryTypes() returns undefined for other reports
      Injury_Types__c: getInjuryTypes(),
      Trailing_Period__c: data.periodType,
      Trailing_Period_Quantity__c: data.periodLength,
    };

    try {
      const res = await doPostSubscription({ subscription });
      toast.success(res.data.message);
      setShowSubscriptionModal(false);
    } catch (err: any) {
      console.log({ err });
      toast.error(err.message);
    }
  };

  return (
    <div className="relative" style={{ minHeight: "100vh" }}>
      <BackBtn onClick={() => handleBack()}>
        <ArrowLeftIcon width="30px"></ArrowLeftIcon>
        Back to report categories
      </BackBtn>
      <HeaderSection className="flex flex-col">
        <TitleContainer className="relative mt-7">
          <BigBoldText
            className="text-lg capitalize text-purple-400"
            data-testid="report-title"
          >
            {modifyTitle(reportType)}{" "}
            {(reportType === "repeat-incident" ||
              reportType === "incident-trend") &&
            selectedIncidentTypesLabel
              ? `(${selectedIncidentTypesLabel})`
              : ""}{" "}
          </BigBoldText>
        </TitleContainer>
        <DateModBtnViewBtnContainer className="mt-3 flex flex-col justify-between md:flex-row">
          <ModifyComponent className="modify-btn-container w-full md:w-2/5">
            <div className="flex flex-row">
              <DateDisplayModifyBtnContainer className="mb-4 flex h-10 w-full items-center justify-between border-t border-solid border-gray-200 pl-2">
                <p className="text-sm text-purple-400">
                  {moment(startDate).format("LL")} -{" "}
                  {moment(endDate).format("LL")}
                </p>
              </DateDisplayModifyBtnContainer>

              <ModifyBtn
                onClick={() => setShowFilterModal((prev) => !prev)}
                className="flex h-10 flex-row items-center justify-between border border-gray-200 px-4 py-2 text-sm"
              >
                <GoPencil className="mr-2" />
                Modify
              </ModifyBtn>
            </div>

            <FilterModal
              open={showFilterModal}
              subtitle={reportType}
              closeFn={() => setShowFilterModal(false)}
              filterType="general"
              isModifyMode={showFilterModal}
            />
          </ModifyComponent>
          <ViewandExportReportBtnContainer className="flex h-10 items-end justify-between">
            <ViewBtnsContainer className="mr-2 flex gap-3">
              <div className="flex gap-2">
                {items?.map((item) => (
                  <ActBtn
                    className="text-white h-10 rounded px-4 py-2 text-sm disabled:opacity-50"
                    key={item?.key}
                    // @ts-ignore
                    onClick={item?.label?.props?.onClick}
                  >
                    {/* @ts-ignore */}
                    {item?.label?.props?.children}
                  </ActBtn>
                ))}
              </div>
            </ViewBtnsContainer>
            {/* Only show top level export buttons for reports with multiple tables */}
            {(reportType === "repeat-incident" ||
              reportType === "incident-trend") && (
              <div className="flex gap-2">
                {(reportType === "repeat-incident" ||
                  reportType === "incident-trend") && (
                  <ExportBtn
                    title="Export Excel"
                    className="k-button k-button-md k-button-solid k-rounded-md"
                    onClick={triggerExcelDownload}
                  >
                    Export Excel
                  </ExportBtn>
                )}
                <ExportBtn
                  className="k-button k-button-md k-button-solid k-rounded-md"
                  onClick={triggerPDFDownload}
                >
                  Export PDF
                </ExportBtn>
              </div>
            )}
          </ViewandExportReportBtnContainer>
        </DateModBtnViewBtnContainer>
      </HeaderSection>

      <div className="relative">
        {reportType === "incident-list" && (
          <Fragment>
            {analyticsIncidentList?.length ? (
              analyticsIncidentList?.map((incident: any) => (
                <TableContainer key={incident?.FacilityName}>
                  <DynamicGrid
                    gridCols={incidentListReport()}
                    gridData={incident?.incidents}
                    showTitleBar={true}
                    barTitle={incident?.FacilityName}
                    includeIncidentLink={true}
                    startDate={startDate}
                    endDate={endDate}
                    reportName="Incident List Report"
                    viewStateKey={analyticsGridKey(
                      reportType,
                      startDate,
                      endDate,
                      incidentTypeSelectionKey,
                      incident?.FacilityName
                    )}
                  />
                </TableContainer>
              ))
            ) : (
              <Fragment>
                No information for the selected facilities or duration
              </Fragment>
            )}
          </Fragment>
        )}

        {reportType === "status-report" && (
          <Fragment>
            {analyticsStatusReport?.length ? (
              analyticsStatusReport?.map((incident: any) => (
                <TableContainer key={incident?.FacilityName}>
                  <DynamicGrid
                    gridCols={statusReport()}
                    gridData={incident?.incidents}
                    showTitleBar={true}
                    barTitle={incident?.FacilityName}
                    includeIncidentLink={true}
                    startDate={startDate}
                    endDate={endDate}
                    reportName="Status Report"
                    viewStateKey={analyticsGridKey(
                      reportType,
                      startDate,
                      endDate,
                      incident?.FacilityName
                    )}
                  />
                </TableContainer>
              ))
            ) : (
              <Fragment>
                No information for the selected facilities or duration
              </Fragment>
            )}
          </Fragment>
        )}

        {reportType === "incident-trend" && (
          <Fragment>
            <PDFExport
              margin="1cm"
              ref={incidentTrendPDFExportRef}
              fileName={`Incident Trend Report${selectedIncidentTypesLabel ? ` - ${selectedIncidentTypesLabel}` : ""} ${moment(startDate).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.pdf`}
              paperSize="A1"
              landscape={true}
              repeatHeaders={true}
            >
              {/* No .pdf-export-tight wrapper here: the trend report's
                  PDFExport wraps the visible-on-screen DOM (not an offscreen
                  mirror), so the wrapper class would also retighten the live
                  UI. Trend PDF exports give up the column tightening to keep
                  the UI clean. */}
              <TableContainer key={trendSelectedFacility?.label}>
                <DynamicGrid
                  gridCols={[...trendsCol]}
                  gridData={[...trendsData]}
                  showTitleBar={true}
                  showSearchBar={false}
                  barTitle={trendSelectedFacility?.label}
                  showExportRow={false}
                  includeIncidentLink={true}
                  startDate={startDate}
                  endDate={endDate}
                  reportName="Trend Report"
                  viewStateKey={analyticsGridKey(
                    reportType,
                    startDate,
                    endDate,
                    incidentTypeSelectionKey,
                    trendSelectedFacility?.label
                  )}
                  scrollable="scrollable"
                  showPagination={false}
                />
              </TableContainer>
              <div>
                <MapContainer id="line-chart">
                  {lineChartData?.datasets?.map((dataset: any) => (
                    <div key={dataset.borderColor} className="mb-7 mt-3">
                      <LineChart
                        dataset={dataset}
                        labels={lineChartData?.labels}
                        onPointClick={(
                          month: string,
                          year: string,
                          label: string
                        ) => {
                          setMonth(month);
                          setYear(year);
                          setIncidentType(label);
                        }}
                      />
                    </div>
                  ))}
                </MapContainer>
              </div>
          </PDFExport>
          <ExcelExport
              data={trendsData}
              ref={incidentTrendExcelExportRef}
              fileName={`Trend Report - ${trendSelectedFacility?.label} ${selectedIncidentTypesLabel ? ` - ${selectedIncidentTypesLabel}` : ""} ${moment(startDate).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.xlsx`}
            >
              <ExcelExportColumnGroup
                title={`Trend Report - ${trendSelectedFacility?.label} ${selectedIncidentTypesLabel ? ` - ${selectedIncidentTypesLabel}` : ""}: ${moment(startDate).format("LL")} to ${moment(endDate).format("LL")}`}
                headerCellOptions={{ textAlign: "left" }}
              >
                {trendsCol.map((col) => (
                  <ExcelExportColumn
                    key={col.accessor}
                    field={col.accessor}
                    title={col.Header}
                  />
                ))}
              </ExcelExportColumnGroup>
            </ExcelExport>
          </Fragment>
        )}

        {reportType === "repeat-incident" && (
          <Fragment>
            {repeatIncidentGroups?.length ? (
              repeatIncidentGroups.map((facility: any) => (
                <div key={facility.facilityName} className="mb-10">
                  <BigBoldText className="text-base text-purple-400">
                    {facility.facilityName}
                  </BigBoldText>

                  {facility.residents.map((resident: any) => (
                    <div
                      key={`${facility.facilityName}-${resident.residentName}`}
                      className="mt-6 rounded-md border border-gray-200 p-4 shadow-sm"
                    >
                      <div className="mb-4 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                        <div className="text-lg font-semibold text-gray-700">
                          {resident.residentName}
                        </div>
                        <span className="text-sm uppercase tracking-wide text-gray-500">
                          {resident.incidentCount} incident
                          {resident.incidentCount !== 1 ? "s" : ""}
                        </span>
                      </div>

                      <DynamicGrid
                        gridCols={repeatIncidentColumns}
                        gridData={resident.incidents}
                        startDate={startDate}
                        endDate={endDate}
                        reportName="Repeat Incident Report"
                        viewStateKey={analyticsGridKey(
                          reportType,
                          startDate,
                          endDate,
                          incidentTypeSelectionKey,
                          facility.facilityName,
                          resident.residentName
                        )}
                        facilityName={facility.facilityName}
                        residentName={resident.residentName}
                        onOptionalColumnsChange={createOptionalColumnsChangeHandler(
                          'repeat-incident',
                          `${facility.facilityName}-${resident.residentName}`
                        )}
                      />
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <Fragment>
                No information for the selected facilities or duration
              </Fragment>
            )}
          </Fragment>
        )}

        {reportType === "incident-rate" && (
          <Fragment>
            {analyticsIncidentRate && analyticsIncidentRate.length ? (
              <TableContainer>
                <DynamicGrid
                  gridCols={[
                    { Header: "Facility", accessor: "FacilityName" },
                    { Header: "Licensed Beds", accessor: "LicensedBeds" },
                    { Header: "Incidents", accessor: "NumberOfIncidents" },
                    { Header: "Rate per 1,000 Beds", accessor: "RatePer1000Beds" },
                  ]}
                  gridData={analyticsIncidentRate}
                  showTitleBar={true}
                  barTitle={`Incident Rate Report`}
                  includeIncidentLink={false}
                  startDate={startDate}
                  endDate={endDate}
                  reportName="Incident Rate Report"
                  viewStateKey={analyticsGridKey(reportType, startDate, endDate)}
                />
              </TableContainer>
            ) : (
              <Fragment>No information for the selected facilities or duration</Fragment>
            )}
          </Fragment>
        )}

        {reportType === "injury-report" && (
          <Fragment>
            {transformedInjuryReport?.length ? (
              transformedInjuryReport?.map((facility: any) => (
                <TableContainer key={facility?.FacilityName}>
                  <DynamicGrid
                    gridCols={injuryReportCols()}
                    gridData={facility?.incidents}
                    showTitleBar={true}
                    barTitle={facility?.FacilityName}
                    includeIncidentLink={true}
                    startDate={startDate}
                    endDate={endDate}
                    reportName="Injury Report"
                    viewStateKey={analyticsGridKey(
                      reportType,
                      startDate,
                      endDate,
                      facility?.FacilityName
                    )}
                  />
                </TableContainer>
              ))
            ) : (
              <Fragment>
                No information for the selected facilities or duration
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
      {showSubscriptionModal && (
        <Modalant
          isLoading={isLoadingSubscription}
          className="items-center md:w-6/12"
          isOpen={showSubscriptionModal}
          isClose={() => setShowSubscriptionModal(false)}
        >
          <EmailSubscriptionForm
            handleSubmit={handleFormSubmit}
            handleClose={() => setShowSubscriptionModal(false)}
            isFacility={true}
            isLoading={isLoadingSubscription}
            title={getReportType()}
          />
        </Modalant>
      )}

      {/* Hidden PDF Export for Repeat Incidents - matches DynamicGrid styling */}
      {reportType === "repeat-incident" && repeatIncidentGroups?.length > 0 && (
        <div
          style={{ position: "absolute", left: EXPORT_OFFSCREEN_LEFT, top: 0 }}
        >
          <div
            style={{ width: "2000px", paddingBottom: EXPORT_SECTION_SPACING }}
          >
            <PDFExport
              margin="1cm"
              ref={repeatIncidentPDFExportRef}
              fileName={`Repeat Incident Report${selectedIncidentTypesLabel ? ` - ${selectedIncidentTypesLabel}` : ""} ${moment(startDate).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.pdf`}
              paperSize="A1"
              landscape={true}
              repeatHeaders={true}
            >
              <div className="pdf-export-tight">
              {repeatIncidentGroups.map(
                (facility: any, facilityIndex: number) => (
                  <div
                    key={`export-facility-${facilityIndex}`}
                    style={{ marginBottom: EXPORT_SECTION_SPACING }}
                  >
                    {facility.residents.map(
                      (resident: any, residentIndex: number) => (
                        <div
                          key={`export-resident-${facilityIndex}-${residentIndex}`}
                          style={{
                            marginBottom: EXPORT_BANNER_GAP,
                            pageBreakAfter: "auto",
                          }}
                        >
                          <div style={{ marginBottom: "8px" }}>
                            <TitleBar
                              style={{
                                marginBottom: 8,
                                paddingTop: 10,
                                paddingBottom: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <div style={{ textAlign: "center" }}>
                                  <div
                                    style={{ fontSize: 28, fontWeight: 700 }}
                                  >
                                    {facility.facilityName} -{" "}
                                    {resident.residentName}
                                  </div>
                                  <div
                                    style={{ fontSize: 24, marginTop: 6 }}
                                  >{`${resident.incidentCount} ${resident.incidentCount !== 1 ? "incidents" : "incident"}`}</div>
                                  <div
                                    style={{ fontSize: 22, marginTop: 6 }}
                                  >{`Repeat Incident Report: ${moment(startDate).format("LL")} - ${moment(endDate).format("LL")}`}</div>
                                </div>
                              </div>
                            </TitleBar>
                          </div>
                          <Grid
                            data={resident.incidents}
                            pageable={false}
                            sortable={false}
                            scrollable="none"
                          >
                            {repeatIncidentReport().map((col: any) => (
                              <GridColumn
                                key={col.accessor}
                                field={col.accessor}
                                title={col.Header}
                              />
                            ))}
                          </Grid>
                        </div>
                      )
                    )}
                  </div>
                )
              )}
              </div>
            </PDFExport>

            {/* Hidden Excel Export for Repeat Incidents - matches DynamicGrid styling */}
            <ExcelExport
              data={repeatIncidentExportRows}
              ref={repeatIncidentExcelExportRef}
              fileName={`Repeat Incident Report${selectedIncidentTypesLabel ? ` - ${selectedIncidentTypesLabel}` : ""} ${moment(startDate).format("MM-DD-YYYY")} to ${moment(endDate).format("MM-DD-YYYY")}.xlsx`}
            >
              <ExcelExportColumnGroup
                title={`Repeat Incident Report${selectedIncidentTypesLabel ? ` - ${selectedIncidentTypesLabel}` : ""}: ${moment(startDate).format("LL")} to ${moment(endDate).format("LL")}`}
                headerCellOptions={{ textAlign: "left" }}
              >
                {repeatIncidentExcelColumnDefs.map((col) => (
                  <ExcelExportColumn
                    key={col.field}
                    field={col.field}
                    title={col.title}
                  />
                ))}
              </ExcelExportColumnGroup>
            </ExcelExport>
          </div>
        </div>
      )}

    </div>
  );
};

export default Report;